import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import translate from '../../translate';
import StripeService from '../../../services/stripeService';
import CustomRadio from './CustomRadio';
import axios from 'axios';
import {
  compose,
  PAYMENT_METHOD as PaymentMethodType,
} from '../../../services/utilityFunctions';
import withCSRF from '../../withCSRF';
import UserProp from '../../propTypes/userProp';

// Stripe / axios / runtime can throw object errors. JSX must only ever render a
// string here — otherwise React crashes with "Objects are not valid as a React
// child" and unmounts the modal subtree.
const toErrorKey = e => {
  if (!e) return null;
  if (typeof e === 'string') return e;
  return e.message || e.code || 'ERROR_UNKNOWN';
};

function StripeForm({
  t,
  id,
  label,
  onFinish,
  onCancel,
  isCheckout,
  attachToUser,
  onChangeAttachToUser,
  showSaveCard,
  isSubscription,
  loggedUser,
  _csrf,
  isUserPlan,
}) {
  const cardEl = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const elements = StripeService.elements();
    const card = elements.create('card', { hidePostalCode: true });
    cardEl.current = card;
    card.addEventListener('change', ({ error: inputErr }) =>
      setError(toErrorKey(StripeService.handleError(inputErr))),
    );
    card.mount(`#${id}`);

    return () => cardEl.current.destroy();
  }, []);

  const handleAdd = async () => {
    try {
      if (isUserPlan && loggedUser) {
        const { data: response } = await axios.post(
          '/api/payment/methods/intent/future',
          {
            paymentMethodType: PaymentMethodType.CREDIT_CARD,
          },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'License',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        );

        const result = await StripeService.confirmCardSetup(
          response.data.client_secret,
          cardEl.current,
          loggedUser,
        );
        onFinish({
          ...result,
          isPaymentMethod: true,
        });
        return;
      }
      const pm = await StripeService.createToken(cardEl.current);

      onFinish(pm);
    } catch (err) {
      // Log the raw error so the real upstream cause (e.g. AxiosError from
      // /api/payment/methods/intent/future) is inspectable in the console.
      // eslint-disable-next-line no-console
      console.error('[StripeForm] add card failed', err);
      setError(toErrorKey(err));
    }
  };

  return (
    <div className="stripe-container">
      <label htmlFor={id}>{t(label)}</label>
      <div className="stripe-input-container">
        <div id={id} />
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="stripe-button stripe-cancel-button"
          >
            {t('cancel')}
          </button>
        )}
        <button
          type="button"
          onClick={handleAdd}
          className="stripe-button stripe-finish-button"
        >
          {t('add_card')}
        </button>
      </div>
      <div className="stripe-error-container" role="alert">
        {error ? t(toErrorKey(error)) : ''}
      </div>
      {isCheckout && showSaveCard && !isSubscription && (
        <div className="save-card-wrapper credit-card-info">
          <div className="m-checkbox m-checkbox--state-success">
            <CustomRadio
              id="attachCardToUser"
              value={attachToUser}
              onClick={onChangeAttachToUser}
              checked={attachToUser}
              label={t('credit.card.save')}
            />
          </div>
        </div>
      )}
    </div>
  );
}

StripeForm.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isCheckout: PropTypes.bool,
  attachToUser: PropTypes.bool,
  onChangeAttachToUser: PropTypes.func,
  showSaveCard: PropTypes.bool,
  isSubscription: PropTypes.bool,
  isUserPlan: PropTypes.bool,
  loggedUser: PropTypes.shape(UserProp.propType),
  _csrf: PropTypes.string.isRequired,
};

StripeForm.defaultProps = {
  id: 'stripe-input',
  label: 'stripe_inputLabel',
  isCheckout: false,
  attachToUser: true,
  onChangeAttachToUser: null,
  showSaveCard: true,
  isSubscription: false,
  isUserPlan: false,
  loggedUser: null,
};

export default compose(withCSRF, translate)(StripeForm);
